import { useEffect, useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowRight, ChevronDown, ChevronUp } from '@remarkable/ark-icons';
import {
  ErrorMessage,
  Field,
  Input as ArkInput,
  Label,
  Typography,
} from '@remarkable/ark-web';
import { Controller, useForm } from 'react-hook-form';

import { ButtonClicked, CheckoutStepCompleted } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import {
  CHECKOUT_STEPS,
  ComponentLocations,
} from 'src/analytics/trackingTypes';
import { useCountry, useSetCountry } from 'src/api/queries';
import {
  Button,
  Form,
  Input,
  SplitHorizontalLayout,
  Spinner,
} from 'src/components';
import { CountryPicker2 } from 'src/components/CountryPicker2';
import { StatePicker2 } from 'src/components/StatePicker2';
import { COUNTRIES } from 'src/utils/countryList';

import { useCheckoutForm } from '../checkoutFormStore';
import type {
  CheckoutFormState,
  CheckoutFormStep,
} from '../components/Checkout.types';
import { checkoutAddressFields, toCheckoutAddress } from './checkoutAddress';
import { PromotionCodeInput } from './PromotionCodeInput';
import type { CheckoutPromotion } from './useCheckoutPromotion';

type CheckoutDetailsFormProps = {
  startCheckoutSession: (nextState: CheckoutFormState) => Promise<void>;
  isLoadingSession: boolean;
  promotion: CheckoutPromotion;
};

export function CheckoutDetailsForm({
  startCheckoutSession,
  isLoadingSession,
  promotion,
}: CheckoutDetailsFormProps) {
  const { t } = useLingui();
  const [checkoutFormState] = useCheckoutForm();
  const country = useCountry();
  const setCountry = useSetCountry();
  const form = useForm<Partial<CheckoutFormState>>({
    defaultValues: { ...checkoutFormState },
  });
  const { watch } = form;
  const countryFieldValue = watch('country');
  const isUnitedStates = countryFieldValue === 'US';
  const isCanada = countryFieldValue === 'CA';
  const [showBillingInfo, setShowBillingInfo] = useState(false);

  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      if (checkoutAddressFields.some((field) => field === name)) {
        // If the user changes address / country, we need the promotion to be
        // reset so that they can re-apply it, since we don't know if the
        // promotion is valid for the new address.
        promotion.reset();
      }
    });

    return () => subscription.unsubscribe();
  }, [form.watch, promotion.reset]);

  useEffect(() => {
    if (!country.isLoading && !form.getValues('country')) {
      form.setValue('country', country.data?.value);
    }
  }, [country.isLoading]);

  if (country.isLoading) {
    return <Spinner />;
  }

  return (
    <div className="flex flex-col gap-16">
      <Form
        {...form}
        onSubmit={async (data) => {
          const nextState = {
            ...checkoutFormState,
            ...data,
            currentStep: 'PAYMENT' as CheckoutFormStep,
          };

          tracker.trackEvent(
            new CheckoutStepCompleted({
              checkout_step:
                CHECKOUT_STEPS.CHECKOUT_V2_CONTACT_DETAILS_COMPLETED,
              coupon: promotion.appliedPromotion?.preview.appliedPromotion.name,
            })
          );
          await startCheckoutSession(nextState);
        }}
      >
        <div className="flex flex-col gap-16">
          <SplitHorizontalLayout>
            <Input
              name="firstName"
              label={t`First name`}
              autoComplete="given-name"
              required
            />
            <Input
              name="lastName"
              label={t`Last name`}
              autoComplete="family-name"
              required
            />
          </SplitHorizontalLayout>

          <Controller
            control={form.control}
            name="country"
            rules={{
              required: t`Country is required`,
              onChange(event: React.ChangeEvent<HTMLSelectElement>) {
                const newCountry = COUNTRIES.find(
                  (c) => c.value === event.target.value
                );

                if (!newCountry) {
                  throw new Error('Country does not exist!');
                }

                void setCountry(newCountry);
              },
            }}
            render={({ field, fieldState }) => {
              return (
                <Field className="w-full">
                  <Label htmlFor={field.name}>
                    <Trans>Country</Trans>
                  </Label>

                  <CountryPicker2
                    id={field.name}
                    hasError={!!fieldState.error}
                    aria-invalid={!!fieldState.error}
                    aria-errormessage={fieldState.error?.message}
                    {...field}
                  />

                  {fieldState.error?.message && (
                    <ErrorMessage data-cy="country-error-message">
                      {fieldState.error.message}
                    </ErrorMessage>
                  )}
                </Field>
              );
            }}
          />

          <SplitHorizontalLayout>
            {isUnitedStates && (
              <>
                <Controller
                  control={form.control}
                  name="state"
                  rules={{
                    required: t`State is required`,
                  }}
                  shouldUnregister
                  render={({ field, fieldState }) => {
                    return (
                      <Field className="w-full flex-1">
                        <Label htmlFor={field.name}>
                          <Trans context="Address level">State</Trans>
                        </Label>

                        <StatePicker2
                          country={'US'}
                          id={field.name}
                          hasError={!!fieldState.error}
                          aria-invalid={!!fieldState.error}
                          aria-errormessage={fieldState.error?.message}
                          {...field}
                        />

                        {fieldState.error?.message && (
                          <ErrorMessage data-cy="state-error-message">
                            {fieldState.error.message}
                          </ErrorMessage>
                        )}
                      </Field>
                    );
                  }}
                />

                <Controller
                  control={form.control}
                  name="postalCode"
                  rules={{
                    required: t`Zip code is required`,
                    validate: {
                      length: (value: string | undefined) => {
                        if (!value) return t`ZIP code must be provided`;
                        const zipPattern = /^\d{5}([-]\d{4})?$/i;
                        return (
                          zipPattern.test(value) ||
                          t`Please enter a valid zip code for the United States, for example 90210 or 90210-1234.`
                        );
                      },
                    },
                  }}
                  shouldUnregister
                  render={({ field, fieldState }) => (
                    <Field className="w-full min-w-0 flex-1">
                      <Label htmlFor={field.name}>
                        <Trans>Zip code</Trans>
                      </Label>
                      <ArkInput
                        data-cy="postal-code-input"
                        id={field.name}
                        inputMode="numeric"
                        {...field}
                        value={field.value ?? ''}
                        hasError={!!fieldState.error}
                        aria-invalid={!!fieldState.error}
                        aria-errormessage={fieldState.error?.message}
                      />

                      {fieldState.error?.message && (
                        <ErrorMessage data-cy="postal-code-error-message">
                          {fieldState.error.message}
                        </ErrorMessage>
                      )}
                    </Field>
                  )}
                />
              </>
            )}

            {isCanada && (
              <Controller
                control={form.control}
                name="province"
                rules={{
                  required: t`Province is required`,
                }}
                shouldUnregister
                render={({ field, fieldState }) => {
                  const provinceName = field.value;
                  return (
                    <Field className="w-full">
                      <Label htmlFor={field.name}>
                        <Trans context="Canadian province">
                          Province {provinceName}
                        </Trans>
                      </Label>

                      <StatePicker2
                        country={'CA'}
                        id={field.name}
                        hasError={!!fieldState.error}
                        aria-invalid={!!fieldState.error}
                        aria-errormessage={fieldState.error?.message}
                        {...field}
                      />

                      {fieldState.error?.message && (
                        <ErrorMessage data-cy="state-error-message">
                          {fieldState.error.message}
                        </ErrorMessage>
                      )}
                    </Field>
                  );
                }}
              />
            )}
          </SplitHorizontalLayout>

          <div className="my-16 flex flex-col gap-16">
            <button
              data-cy="billing-info-optional"
              aria-expanded={showBillingInfo}
              className="relative h-32 w-full cursor-pointer border-none bg-none text-left body-lg"
              onClick={(e) => {
                tracker.trackEvent(
                  new ButtonClicked({
                    component_location:
                      ComponentLocations.CHECKOUT.CONNECT_CHECKOUT,
                    text: e.currentTarget.innerText,
                    action: 'toggle billing info in checkout',
                  })
                );
                setShowBillingInfo(!showBillingInfo);
              }}
              type="button"
            >
              <Typography
                variant="interface-lg"
                fontWeight="semibold"
                leadingNone
              >
                <Trans>
                  Billing information{' '}
                  <span className="font-regular">(optional)</span>
                </Trans>
              </Typography>
              {!showBillingInfo ? (
                <ChevronDown
                  size={24}
                  className="absolute top-0 right-12 text-default"
                />
              ) : (
                <ChevronUp
                  size={24}
                  className="absolute top-0 right-12 text-default"
                />
              )}
            </button>

            {showBillingInfo && (
              <>
                <Input
                  data-cy="billing-address1"
                  label={t`Billing address 1`}
                  autoComplete="street-address"
                  name="billingAddress1"
                />
                <Input
                  data-cy="billing-address2"
                  name="billingAddress2"
                  label={t`Billing address 2`}
                />

                <SplitHorizontalLayout>
                  {!isUnitedStates && (
                    <Input
                      data-cy="billing-post-code"
                      name="postalCode"
                      label={t`Postal code`}
                      // Not numeric-only: many countries (e.g. Canada, the UK)
                      // have alphanumeric postal codes.
                      autoComplete="postal-code"
                    />
                  )}
                  <Input
                    data-cy="billing-city"
                    name="city"
                    label={t`City`}
                    autoComplete="address-level2"
                  />
                </SplitHorizontalLayout>
              </>
            )}

            <PromotionCodeInput
              promotion={promotion}
              isValidatingAddress={form.formState.isValidating}
              onApply={async () => {
                const isValid = await form.trigger(checkoutAddressFields, {
                  shouldFocus: true,
                });
                if (isValid) {
                  promotion.apply(toCheckoutAddress(form.getValues()));
                }
              }}
            />
          </div>

          <Button
            variant="primary"
            type="submit"
            className="w-full ll:w-fit"
            data-cy="continue-button"
            loading={form.formState.isSubmitting || isLoadingSession}
            disabled={
              form.formState.isSubmitting ||
              isLoadingSession ||
              !promotion.canContinue
            }
            analytics={{
              text: t`Continue`,
              location: ComponentLocations.CHECKOUT.CONTINUE,
              action: 'continue to payment',
            }}
          >
            <span>
              <Trans>Continue</Trans>
            </span>
            <ArrowRight size={24} />
          </Button>
        </div>
      </Form>
    </div>
  );
}
